import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATS = [["MATERIAL", "Material"], ["LABOUR", "Labour"], ["SUBCONTRACT", "Subcontract"], ["PLANT", "Equipment"], ["OTHER", "Other"]];

export const ratesTotal = (lines) => (lines || []).reduce((s, l) => s + (Number(l.rate) || 0), 0);

/** Button for one template activity: edit its cost lines (a rate per unit of the activity). */
export function TemplateRatesButton({ lines, onChange }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const total = ratesTotal(lines);

  const openIt = () => { setRows((lines || []).map((l) => ({ ...l }))); setOpen(true); };
  const upd = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const save = () => { onChange(rows.filter((r) => (r.description || "").trim())); setOpen(false); };

  return (
    <>
      <Button type="button" size="sm" variant="outline" className="h-8 whitespace-nowrap" onClick={openIt} data-testid="template-rates-button">
        ₹ {total ? `${total.toLocaleString("en-IN")}/unit` : "Rates"}
      </Button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-xl" data-testid="template-rates-dialog">
            <DialogHeader>
              <DialogTitle>Cost per unit</DialogTitle>
              <DialogDescription>Rate for ONE unit of this activity (e.g. ₹ per m³). When a project is built from this template, each line becomes a cost line on the activity and is multiplied by that project&apos;s quantity.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {rows.length === 0 && <p className="py-2 text-sm text-muted-foreground">No cost lines yet.</p>}
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[130px_1fr_110px_32px] items-center gap-2">
                  <Select value={r.category} onValueChange={(v) => upd(i, { category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={r.description} onChange={(e) => upd(i, { description: e.target.value })} />
                  <Input type="number" min="0" placeholder="Rate" value={r.rate} onChange={(e) => upd(i, { rate: e.target.value })} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">Add:</span>
                {CATS.map(([k, l]) => (
                  <Button key={k} type="button" size="sm" variant="outline" onClick={() => setRows((x) => [...x, { category: k, description: l, rate: "" }])}><Plus className="h-3 w-3" /> {l}</Button>
                ))}
              </div>
            </div>
            <div className="flex justify-between border-t border-border pt-3 text-sm">
              <span className="text-muted-foreground">Total per unit</span>
              <span className="font-mono font-semibold">₹{ratesTotal(rows).toLocaleString("en-IN")}</span>
            </div>
            <DialogFooter><Button type="button" onClick={save}>Done</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
